import RailSwitch from '../RailSwitch'
/* Port of the sanitized Neon Treviso overlay supplied on 3 Nov 2026.
 * Preserve element picking, CSS anchors, relative coordinates and relayout;
 * NOLOS owns authenticated authorship, page/query scope and storage. */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/useAuth'
import { apiFetch, ApiError } from '../../lib/api'
import { useRead } from '../navigation/useRead'
import { stampDateTime } from '../routeError'
import { appVersion, useReviewComments, type Anchor, type ReviewComment } from './api'
import './review-comments.css'

const ROOT_ATTR = 'data-review-ui'
/** The pick mask still owns clicks; wheel input scrolls the actual pane
 * beneath it, including nested scrollers, without activating that pane. */
function scrollBehindPicker(event: WheelEvent) {
  if (event.ctrlKey || event.metaKey) return
  const target = document
    .elementsFromPoint(event.clientX, event.clientY)
    .find((element) => !element.closest('[' + ROOT_ATTR + ']'))
  if (!target) return
  const line = parseFloat(getComputedStyle(target).lineHeight) || 16
  let dx = event.deltaX * (event.deltaMode === 1 ? line : event.deltaMode === 2 ? innerWidth : 1)
  let dy = event.deltaY * (event.deltaMode === 1 ? line : event.deltaMode === 2 ? innerHeight : 1)
  if (event.shiftKey && dx === 0) {
    dx = dy
    dy = 0
  }
  let consumed = false
  for (let node: Element | null = target; node; node = node.parentElement) {
    if (!(node instanceof HTMLElement)) continue
    const style = getComputedStyle(node)
    const root = node === document.scrollingElement
    const canX =
      /auto|scroll|overlay/.test(style.overflowX) || (root && !/hidden|clip/.test(style.overflowX))
    const canY =
      /auto|scroll|overlay/.test(style.overflowY) || (root && !/hidden|clip/.test(style.overflowY))
    const x = node.scrollLeft,
      y = node.scrollTop
    if (canX && node.scrollWidth > node.clientWidth) node.scrollLeft += dx
    if (canY && node.scrollHeight > node.clientHeight) node.scrollTop += dy
    const usedX = node.scrollLeft - x,
      usedY = node.scrollTop - y
    consumed ||= usedX !== 0 || usedY !== 0
    dx = Math.sign(dx) * Math.max(0, Math.abs(dx) - Math.abs(usedX))
    dy = Math.sign(dy) * Math.max(0, Math.abs(dy) - Math.abs(usedY))
    if (canX && /contain|none/.test(style.overscrollBehaviorX) && dx) {
      dx = 0
      consumed = true
    }
    if (canY && /contain|none/.test(style.overscrollBehaviorY) && dy) {
      dy = 0
      consumed = true
    }
    if (!dx && !dy) break
  }
  if (consumed) event.preventDefault()
}

type Point = { left: number; top: number }
type Draft = { anchor: Anchor; doc: { x: number; y: number }; page: string; url: string } & Point

function cssPath(el: Element): string | null {
  const parts: string[] = []
  let node: Element | null = el
  while (node && node !== document.body && node !== document.documentElement) {
    if (
      node.id &&
      !/^[0-9]/.test(node.id) &&
      document.querySelectorAll(`#${CSS.escape(node.id)}`).length === 1
    ) {
      parts.unshift(`#${CSS.escape(node.id)}`)
      return parts.join(' > ')
    }
    const tag = node.tagName.toLowerCase()
    const parent: Element | null = node.parentElement
    if (!parent) break
    const same = Array.from(parent.children).filter((child) => child.tagName === node!.tagName)
    parts.unshift(same.length > 1 ? `${tag}:nth-of-type(${same.indexOf(node) + 1})` : tag)
    node = parent
  }
  return parts.length ? `body > ${parts.join(' > ')}` : null
}

function describe(el: Element): string {
  const image = el.closest('img') ?? el.querySelector('img')
  if (el.tagName === 'IMG' || (image && !(el.textContent ?? '').trim())) {
    const photo = (el.tagName === 'IMG' ? el : image) as HTMLImageElement
    return `Image: ${photo.alt || photo.currentSrc.split('/').pop() || 'image'}`
  }
  // Deliberately never reads input.value (including password fields).
  const text = (el.textContent ?? '').replace(/\s+/g, ' ').trim()
  return text.length > 120 ? `${text.slice(0, 117)}…` : text
}

function anchoredElement(selector: string | null): Element | null {
  try {
    return selector ? document.querySelector(selector) : null
  } catch {
    // A clipped/obsolete CSS path still has its original document fallback.
    return null
  }
}

function place(comment: Pick<ReviewComment, 'anchor' | 'doc'>): Point {
  const element = anchoredElement(comment.anchor.selector)
  const max = document.documentElement.clientWidth - 16
  const clampX = (x: number) => Math.min(max, Math.max(16, x))
  if (element) {
    const rect = element.getBoundingClientRect()
    if (rect.width > 0 && rect.height > 0)
      return {
        left: clampX(rect.left + window.scrollX + comment.anchor.rx * rect.width),
        top: rect.top + window.scrollY + comment.anchor.ry * rect.height,
      }
  }
  return {
    left: clampX((comment.doc.x / 100) * document.documentElement.clientWidth),
    top: comment.doc.y,
  }
}

export default function ReviewComments() {
  const { user } = useAuth()
  const { pathname } = useLocation()
  if (!user || !['admin', 'manager'].includes(user.role) || pathname === '/v2/comments') return null
  return <EnabledOverlay />
}

function EnabledOverlay() {
  const capability = useRead<{ enabled: boolean }>('/api/review-comments/capabilities')
  return capability.value?.enabled ? <ReviewOverlay /> : null
}

function ReviewOverlay() {
  const location = useLocation()
  const page = location.pathname + location.search
  const read = useReviewComments(page)
  const currentKey = useRef(read.key)
  currentKey.current = read.key
  const [mode, setMode] = useState<'idle' | 'picking' | 'writing'>('idle')
  const [draft, setDraft] = useState<Draft | null>(null)
  const [showPins, setShowPins] = useState(true)
  const [showDone, setShowDone] = useState(false)
  const [openPin, setOpenPin] = useState<string | null>(null)
  const [positions, setPositions] = useState<Record<string, Point>>({})
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [toast, setToast] = useState('')
  const commentButton = useRef<HTMLButtonElement>(null)
  const picker = useRef<HTMLDivElement>(null)
  const sheet = useRef<HTMLDialogElement>(null)
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const focusedHash = useRef<string | null>(null)
  const { comments } = read
  const activeDraft = draft?.page === page ? draft : null
  const visible = comments.filter((comment) => showDone || comment.status === 'open')

  useEffect(() => {
    setMode('idle')
    setDraft(null)
    setOpenPin(null)
    setError('')
    setToast('')
    setBusy(false)
    focusedHash.current = null
  }, [read.key])

  const relayout = useCallback(() => {
    const next: Record<string, Point> = {}
    for (const comment of comments) next[comment.id] = place(comment)
    setPositions(next)
  }, [comments])

  useLayoutEffect(() => {
    let frame = 0
    const schedule = () => {
      if (!frame)
        frame = requestAnimationFrame(() => {
          frame = 0
          relayout()
        })
    }
    schedule()
    const observer = new ResizeObserver(schedule)
    observer.observe(document.body)
    window.addEventListener('resize', schedule)
    window.addEventListener('scroll', schedule, true)
    window.addEventListener('pointerup', schedule)
    document.addEventListener('click', schedule, true)
    const timer = window.setInterval(schedule, 1500)
    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
      window.removeEventListener('resize', schedule)
      window.removeEventListener('scroll', schedule, true)
      window.removeEventListener('pointerup', schedule)
      document.removeEventListener('click', schedule, true)
      clearInterval(timer)
    }
  }, [relayout])

  useEffect(
    () => () => {
      if (toastTimer.current) clearTimeout(toastTimer.current)
    },
    []
  )

  useEffect(() => {
    const focusKey = read.key + location.hash
    if (focusedHash.current === focusKey) return
    const id = location.hash.startsWith('#review-comment-')
      ? location.hash.slice('#review-comment-'.length)
      : null
    const selected = comments.find((comment) => comment.id === id)
    if (!selected) return
    focusedHash.current = focusKey
    setShowPins(true)
    if (selected.status === 'resolved') setShowDone(true)
    setOpenPin(selected.id)
    anchoredElement(selected.anchor.selector)?.scrollIntoView({ block: 'center' })
    const frame = requestAnimationFrame(relayout)
    return () => cancelAnimationFrame(frame)
  }, [location.hash, comments, relayout, read.key])

  useEffect(() => {
    if (mode !== 'writing' || !activeDraft) return
    const dialog = sheet.current
    const before = document.activeElement
    dialog?.showModal()
    return () => {
      dialog?.close()
      if (before instanceof HTMLElement && before.isConnected) before.focus()
    }
  }, [mode, activeDraft])

  function cancel() {
    if (busy) return
    setMode('idle')
    setDraft(null)
    setError('')
    commentButton.current?.focus()
  }

  useEffect(() => {
    if (mode !== 'picking' || !picker.current) return
    const node = picker.current
    node.addEventListener('wheel', scrollBehindPicker, { passive: false })
    return () => node.removeEventListener('wheel', scrollBehindPicker)
  }, [mode])

  useEffect(() => {
    if (mode !== 'picking') return
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMode('idle')
        commentButton.current?.focus()
      }
    }
    window.addEventListener('keydown', escape)
    return () => window.removeEventListener('keydown', escape)
  }, [mode])

  function onPick(event: React.MouseEvent<HTMLDivElement>) {
    const x = event.clientX,
      y = event.clientY
    const elements = document
      .elementsFromPoint(x, y)
      .filter((element) => !element.closest(`[${ROOT_ATTR}]`))
    const element = elements[0] ?? document.body
    const rect = element.getBoundingClientRect()
    const left = x + window.scrollX,
      top = y + window.scrollY
    setDraft({
      anchor: {
        selector: cssPath(element),
        rx: rect.width ? Math.min(1, Math.max(0, (x - rect.left) / rect.width)) : 0,
        ry: rect.height ? Math.min(1, Math.max(0, (y - rect.top) / rect.height)) : 0,
        snippet: describe(element),
      },
      doc: { x: (left / document.documentElement.scrollWidth) * 100, y: Math.round(top) },
      left,
      top,
      page,
      url: window.location.href,
    })
    setText('')
    setError('')
    setMode('writing')
  }

  async function send() {
    if (!activeDraft || busy) return
    if (text.trim().length < 2) return setError('Write a comment.')
    const key = read.key
    setBusy(true)
    setError('')
    try {
      const saved = await apiFetch<ReviewComment>('/api/review-comments', {
        method: 'POST',
        body: JSON.stringify({
          page: activeDraft.page,
          url: activeDraft.url,
          app_version: appVersion(),
          text,
          anchor: activeDraft.anchor,
          doc: activeDraft.doc,
          viewport: window.innerWidth,
        }),
      })
      if (currentKey.current !== key) return
      read.update((items) => [...items, saved])
      setMode('idle')
      setDraft(null)
      setShowPins(true)
      setToast('Comment saved.')
      if (toastTimer.current) clearTimeout(toastTimer.current)
      toastTimer.current = setTimeout(() => setToast(''), 3000)
    } catch {
      if (currentKey.current === key)
        setError(
          'Could not confirm that the comment was saved. Check the comments list before retrying.'
        )
    } finally {
      if (currentKey.current === key) setBusy(false)
    }
  }

  if (read.error instanceof ApiError && [403, 404].includes(read.error.status)) return null
  const openCount = comments.filter((comment) => comment.status === 'open').length
  const doneCount = comments.length - openCount
  return (
    <>
      <span className="review-comments-marker" hidden />
      <div className="review-rail-controls" {...{ [ROOT_ATTR]: '' }} aria-label="Page comments">
        <RailSwitch
          ref={commentButton}
          label="Comment"
          className="review-comment-toggle"
          on={mode === 'picking'}
          disabled={read.loading || !!read.error || busy || mode === 'writing'}
          onToggle={() => {
            if (mode === 'picking') cancel()
            else {
              setOpenPin(null)
              setMode('picking')
            }
          }}
        />
        <Link to="/v2/comments">Comments ›</Link>
        <details className="review-pins-menu">
          <summary>Pins ({openCount})</summary>
          <button
            type="button"
            onClick={() => setShowPins((value) => !value)}
            aria-pressed={showPins}
          >
            {showPins ? 'Hide' : 'Show'} pins ({openCount})
          </button>
          {doneCount > 0 ? (
            <button
              type="button"
              onClick={() => setShowDone((value) => !value)}
              aria-pressed={showDone}
            >
              Resolved ({doneCount})
            </button>
          ) : null}
        </details>
        {read.loading ? <p role="status">Loading comments…</p> : null}
        {read.error ? (
          <>
            <p role="alert">Comments could not be loaded.</p>
            <button type="button" onClick={read.reload}>
              Retry comments
            </button>
          </>
        ) : null}
      </div>
      {createPortal(
        <div className="review-overlay" {...{ [ROOT_ATTR]: '' }}>
          {showPins && (
            <div className="review-pins">
              {visible.map((comment, index) => {
                const point = positions[comment.id]
                if (!point) return null
                const resolved = comment.status === 'resolved'
                return (
                  <div key={comment.id} className="review-pin-anchor" style={point}>
                    <button
                      type="button"
                      className={`review-pin${resolved ? ' is-resolved' : ''}`}
                      onClick={() => {
                        setToast('')
                        setOpenPin(openPin === comment.id ? null : comment.id)
                      }}
                      aria-label={`Comment by ${comment.author}`}
                      aria-expanded={openPin === comment.id}
                    >
                      {resolved ? '✓' : index + 1}
                    </button>
                    {openPin === comment.id && (
                      <div
                        className="review-pin-note"
                        style={{
                          left:
                            point.left < 170
                              ? -4
                              : point.left > document.documentElement.clientWidth - 170
                                ? undefined
                                : 0,
                          right:
                            point.left > document.documentElement.clientWidth - 170
                              ? -4
                              : undefined,
                          transform:
                            point.left >= 170 &&
                            point.left <= document.documentElement.clientWidth - 170
                              ? 'translateX(-50%)'
                              : undefined,
                        }}
                      >
                        <header>
                          <strong>{comment.author}</strong>
                          <button
                            type="button"
                            onClick={() => setOpenPin(null)}
                            aria-label="Close comment"
                          >
                            ×
                          </button>
                        </header>
                        <small>
                          {stampDateTime(new Date(comment.createdAt))} · {comment.device}
                          {resolved ? ' · resolved' : ''}
                        </small>
                        <p>{comment.text}</p>
                        <Link to="/v2/comments">Open comments list ›</Link>
                      </div>
                    )}
                  </div>
                )
              })}
              {activeDraft && mode === 'writing' && (
                <div
                  className="review-pin is-draft"
                  style={{ left: activeDraft.left, top: activeDraft.top }}
                  aria-hidden="true"
                />
              )}
            </div>
          )}
          {mode === 'picking' && (
            <>
              <div ref={picker} className="review-pick-catcher" onClick={onPick} />
              <div className="review-pick-instruction" role="status">
                <span>Choose the spot you want to comment on.</span>
                <button type="button" onClick={cancel}>
                  Cancel
                </button>
              </div>
            </>
          )}
          {mode === 'writing' && activeDraft && (
            <dialog
              ref={sheet}
              className="review-write-sheet"
              aria-labelledby="review-write-title"
              onCancel={(event) => {
                event.preventDefault()
                cancel()
              }}
              onClick={(event) => {
                if (event.target === event.currentTarget) cancel()
              }}
            >
              <header>
                <h2 id="review-write-title">New comment</h2>
                <button
                  type="button"
                  onClick={cancel}
                  disabled={busy}
                  aria-label="Close comment form"
                >
                  ×
                </button>
              </header>
              {activeDraft.anchor.snippet && (
                <p className="review-snippet">On: “{activeDraft.anchor.snippet}”</p>
              )}
              <label htmlFor="review-comment-text">What would you change here?</label>
              <textarea
                id="review-comment-text"
                value={text}
                onChange={(event) => setText(event.target.value)}
                autoFocus
                rows={4}
                maxLength={2000}
                disabled={busy}
              />
              {error && <p role="alert">{error}</p>}
              <footer>
                <button type="button" onClick={cancel} disabled={busy}>
                  Cancel
                </button>
                <button type="button" className="review-primary" onClick={send} disabled={busy}>
                  {busy ? 'Saving…' : 'Save comment'}
                </button>
              </footer>
            </dialog>
          )}
          {toast && (
            <div className="review-toast" role="status">
              {toast}
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  )
}
